/**
 * Drumul comenzii (etapele de sus) si traseul ei (jurnalul), pe pagina comenzii (21.10.2026,
 * macheta Comenzi). Jurnalul exista in baza de la inceput, dar nu se vedea nicaieri.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { PasTraseu } from '../ui/Traseu'

export interface EvenimentComanda {
  id: number
  action: string
  fromStatus: string | null
  toStatus: string | null
  reason: string | null
  createdAt: string
  actor: { id: number; name: string } | null
}

const cine = (e: EvenimentComanda) => e.actor?.name ?? 'CRM-ul'

/** Un rand de jurnal, spus omeneste. */
function titlu(e: EvenimentComanda): string {
  switch (e.action) {
    case 'created':
      return `Creată de ${cine(e)}${e.reason ? ` · ${e.reason}` : ''}`
    case 'approve':
      return `Aprobată de ${cine(e)}`
    case 'reject':
      return `Respinsă de ${cine(e)}${e.reason ? `: ${e.reason}` : ''}`
    case 'ea_send':
      if (e.toStatus === 'eroare_ea')
        return `Eroare la trimiterea în ERP${e.reason ? `: ${e.reason}` : ''}`
      return e.reason ?? `Trimisă în ERP de ${cine(e)}`
    case 'transition':
      if (e.toStatus === 'asteapta_aprobare')
        return `Trimisă spre aprobare de ${cine(e)} — discount peste prag`
      if (e.toStatus === 'aprobata') return `Trimisă de ${cine(e)} — aprobată automat, sub prag`
      if (e.toStatus === 'anulata') return `Anulată de ${cine(e)}${e.reason ? `: ${e.reason}` : ''}`
      if (e.toStatus === 'ciorna') return `Întoarsă la ciornă${e.reason ? `: ${e.reason}` : ''}`
      return `${cine(e)}: ${e.fromStatus ?? '—'} → ${e.toStatus ?? '—'}`
    default:
      return `${cine(e)}: ${e.action}`
  }
}

/** Jurnalul, de la cel mai vechi la cel mai nou, cum il citeste `Traseu`. */
export function pasiTraseu(evenimente: EvenimentComanda[]): PasTraseu[] {
  return [...evenimente]
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .map((e) => ({ cand: e.createdAt, titlu: titlu(e) }))
}

interface AwbPeComanda {
  pickedUpAt: string | null
  deliveredAt: string | null
  courier: string
  stare: string | null
}

type Fel = 'gata' | 'curent' | 'problema' | 'urmeaza'

const zi = (s: string | null | undefined) =>
  s
    ? new Date(s).toLocaleString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

const STIL: Record<Fel, string> = {
  gata: 'border-b4l-blue/40 bg-b4l-blue-light text-b4l-blue-dark',
  curent: 'border-b4l-amber bg-b4l-amber-light text-b4l-amber-dark',
  problema: 'border-b4l-red bg-b4l-red-light text-b4l-red-dark',
  urmeaza: 'border-b4l-border bg-b4l-surface text-b4l-text-muted',
}

export function DrumulComenzii({
  orderId,
  status,
  createdAt,
  eaSentAt,
  evenimente,
  factura,
}: {
  orderId: number
  status: string
  createdAt: string
  eaSentAt: string | null
  evenimente: EvenimentComanda[]
  factura: { docId: string | null; issueDate: string | null } | null | undefined
}) {
  const inEa = status === 'trimisa_ea'
  // Aceeasi cheie ca blocul „Livrare cu curierul": o singura cerere pentru amandoua.
  const { data } = useQuery<{ awb: AwbPeComanda[] }>({
    queryKey: ['orders', orderId, 'awb'],
    queryFn: async () => (await api.get(`/orders/${orderId}/awb`)).data,
    enabled: inEa,
  })
  const awb = data?.awb?.[0]
  // Jurnalul vine cel mai nou primul: prima gasita e ULTIMA aprobare (dupa o reaprobare).
  const aprobare = evenimente.find(
    (e) => e.action === 'approve' || (e.action === 'transition' && e.toStatus === 'aprobata'),
  )

  const etape: { nume: string; detaliu: string; fel: Fel }[] =
    status === 'anulata'
      ? [{ nume: 'Anulată', detaliu: 'comanda nu mai pleacă', fel: 'urmeaza' }]
      : [
          { nume: 'Creată', detaliu: zi(createdAt), fel: 'gata' },
          {
            nume: status === 'respinsa' ? 'Respinsă' : 'Aprobată',
            detaliu:
              status === 'respinsa'
                ? 'de corectat și retrimis'
                : status === 'asteapta_aprobare'
                  ? 'așteaptă un admin'
                  : aprobare
                    ? aprobare.action === 'approve'
                      ? `${aprobare.actor?.name ?? ''} ${zi(aprobare.createdAt)}`.trim()
                      : 'automat, sub prag'
                    : '',
            fel:
              status === 'respinsa'
                ? 'problema'
                : status === 'asteapta_aprobare'
                  ? 'curent'
                  : status === 'ciorna'
                    ? 'urmeaza'
                    : 'gata',
          },
          {
            nume: 'Trimisă în ERP',
            detaliu: status === 'eroare_ea' ? 'eroare la trimitere' : inEa ? zi(eaSentAt) : '',
            fel:
              status === 'eroare_ea'
                ? 'problema'
                : inEa
                  ? 'gata'
                  : status === 'aprobata'
                    ? 'curent'
                    : 'urmeaza',
          },
          {
            nume: 'Facturată',
            detaliu: factura ? (factura.docId ?? 'în SPV') : inEa ? 'nu apare încă în SPV' : '',
            fel: factura ? 'gata' : inEa ? 'curent' : 'urmeaza',
          },
          {
            nume: 'Predată curierului',
            detaliu: awb?.pickedUpAt
              ? `${awb.courier === 'SAMEDAY' ? 'Sameday' : awb.courier}, ${zi(awb.pickedUpAt)}`
              : inEa
                ? 'încă nu'
                : '',
            fel: awb?.pickedUpAt ? 'gata' : inEa ? 'curent' : 'urmeaza',
          },
          {
            nume: 'Livrată',
            detaliu: awb?.deliveredAt ? zi(awb.deliveredAt) : '',
            fel: awb?.deliveredAt ? 'gata' : 'urmeaza',
          },
        ]

  return (
    <ol
      aria-label="Drumul comenzii"
      className="grid gap-1.5 [grid-template-columns:repeat(auto-fit,minmax(130px,1fr))]"
    >
      {etape.map((e) => (
        <li key={e.nume} className={`grid gap-0.5 rounded-lg border px-2.5 py-2 ${STIL[e.fel]}`}>
          <span className="text-xs font-semibold">{e.nume}</span>
          <span className="truncate text-xs opacity-80" title={e.detaliu}>
            {e.detaliu || '—'}
          </span>
        </li>
      ))}
    </ol>
  )
}
